/**
 * Property settings for one pipeline_properties row.
 *
 * Each deal persists a single `property_setting` row with four headline
 * deal-input columns plus strategy-grouped JSONB blobs:
 *
 *   purchase_price · repair_cost · after_repair_value · monthly_rent (numeric)
 *   flip / brrrr / rental / monthly_cash_flow                        (jsonb)
 *   closing_pct (numeric) · closing_auto (bool)
 *   mao (jsonb) · use_section8_rent (bool)
 *
 * Rehab scope lives in a SEPARATE table — `rehab_line_items` — one row
 * per scope item. This hook fetches those rows alongside the
 * property_setting row and reassembles them into the `settings.rehab`
 * shape the UI already expects (`{ line_items, custom_items }`).
 *
 * Persistence is manual: the detail page builds a full patch and calls
 * `saveSettings` when the user clicks "Save changes". The patch's `rehab`
 * field is a DIFF (`{ insert, update, delete }`), not the full list.
 *
 * Note: until the user runs `pnpm supabase gen types`, the generated
 * Database type doesn't include the new `property_setting` shape — we cast
 * at the query level instead of casting the whole client.
 */
import { useCallback, useMemo } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import type { PostgrestSingleResponse } from "@supabase/supabase-js";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useWorkspace } from "@/hooks/use-workspace";
import {
  composeStrategySettings,
  splitStrategySettings,
  type RawStrategyColumns,
} from "@/lib/property-setting";
import { toUserMessage } from "@/lib/supabase-errors";
import type {
  BrrrrSettings,
  CashflowOverrides,
  FlipSettings,
  MaoSettings,
  RehabLineItemRow,
  RehabLineItemsDiff,
  RehabSettings,
  RentalSettings,
} from "@/types/pipeline";

type DbResp<T> = Promise<PostgrestSingleResponse<T>>;

const TABLE = "property_setting";
const LINE_ITEMS_TABLE = "rehab_line_items";

/** Default values applied when a row doesn't yet exist for the deal. */
export const DEFAULT_DEAL_SETTINGS = {
  purchase_price: null as number | null,
  repair_cost: null as number | null,
  after_repair_value: null as number | null,
  monthly_rent: null as number | null,
  flip: {} as FlipSettings,
  brrrr: {} as BrrrrSettings,
  rental: {} as RentalSettings,
  rehab: { line_items: [], custom_items: [] } as RehabSettings,
  monthly_cash_flow: {} as CashflowOverrides,
  closing_pct: 3,
  closing_auto: true,
  mao: { pct: 70, use_as_purchase: false } as MaoSettings,
  use_section8_rent: false,
} as const;

/**
 * Composed property_setting row — the nested strategy shape the UI consumes.
 * `flip` / `brrrr` / `rental` are reassembled from the split DB columns by
 * `composeStrategySettings` (see `@/lib/property-setting`).
 */
export interface DealSettingsRow {
  id: string;
  pipeline_property_id: string;
  workspace_id: string;
  /** Headline deal inputs — shared across all strategies. */
  purchase_price: number | null;
  repair_cost: number | null;
  after_repair_value: number | null;
  monthly_rent: number | null;
  flip: FlipSettings;
  brrrr: BrrrrSettings;
  rental: RentalSettings;
  monthly_cash_flow: CashflowOverrides;
  closing_pct: number;
  closing_auto: boolean;
  mao: MaoSettings;
  use_section8_rent: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string | null;
}

/**
 * The raw `property_setting` DB row: strategy loan/cost data lives in the split
 * columns (`flip_hard_money`, `brrrr_long_term`, …). Lender / financing-enabled
 * / active-slot now live in the `loans` table, not here.
 * `composeStrategySettings` folds the split columns into {@link DealSettingsRow}.
 */
type RawDealSettingsRow = Omit<DealSettingsRow, "flip" | "brrrr" | "rental"> &
  RawStrategyColumns;

/**
 * Partial patch the UI sends to `saveSettings`. Any omitted key is left
 * untouched. `rehab` is a DIFF describing inserts/updates/deletes against
 * the existing rehab_line_items rows.
 */
export type DealSettingsPatch = Partial<
  Pick<
    DealSettingsRow,
    | "purchase_price"
    | "repair_cost"
    | "after_repair_value"
    | "monthly_rent"
    | "flip"
    | "brrrr"
    | "rental"
    | "monthly_cash_flow"
    | "closing_pct"
    | "closing_auto"
    | "mao"
    | "use_section8_rent"
  >
> & {
  rehab?: RehabLineItemsDiff;
};

function rowsToRehabSettings(rows: RehabLineItemRow[]): RehabSettings {
  const line_items = rows
    .filter((r) => r.category === "standard")
    .map((r) => ({ id: r.id, label: r.label ?? "", amount: Number(r.estimated_cost) || 0 }));
  const custom_items = rows
    .filter((r) => r.category === "custom")
    .map((r) => ({ id: r.id, label: r.label ?? "", amount: Number(r.estimated_cost) || 0 }));
  return { line_items, custom_items };
}

export function useDealSettings(
  pipelinePropertyId: string | null | undefined,
) {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();

  const enabled = Boolean(pipelinePropertyId && currentWorkspace?.id);
  const settingsKey = useMemo(
    () => ["deal_settings", pipelinePropertyId] as const,
    [pipelinePropertyId],
  );
  const lineItemsKey = useMemo(
    () => ["deal_settings_rehab_line_items", pipelinePropertyId] as const,
    [pipelinePropertyId],
  );

  // Fetch property_setting + rehab_line_items together — they're always
  // consumed as a pair. useQueries keeps the two cache entries independent
  // so an invalidation on one doesn't refetch the other.
  const [settingsQuery, lineItemsQuery] = useQueries({
    queries: [
      {
        queryKey: settingsKey,
        enabled,
        queryFn: async (): Promise<RawDealSettingsRow | null> => {
          if (!pipelinePropertyId) return null;
          const { data, error } = (await (
            supabase as unknown as {
              from: (t: string) => {
                select: (s: string) => {
                  eq: (
                    col: string,
                    v: string,
                  ) => { maybeSingle: () => DbResp<RawDealSettingsRow | null> };
                };
              };
            }
          )
            .from(TABLE)
            .select("*")
            .eq("pipeline_property_id", pipelinePropertyId)
            .maybeSingle()) as PostgrestSingleResponse<RawDealSettingsRow | null>;
          if (error) throw error;
          return data ?? null;
        },
      },
      {
        queryKey: lineItemsKey,
        enabled,
        queryFn: async (): Promise<RehabLineItemRow[]> => {
          if (!pipelinePropertyId) return [];
          // Cast at the query boundary: generated supabase types don't yet
          // include the new `pipeline_property_id` / `label` columns on
          // rehab_line_items until `pnpm supabase gen types` is rerun.
          const { data, error } = (await (
            supabase as unknown as {
              from: (t: string) => {
                select: (s: string) => {
                  eq: (
                    col: string,
                    v: string,
                  ) => {
                    order: (
                      c: string,
                      o: { ascending: boolean },
                    ) => DbResp<RehabLineItemRow[]>;
                  };
                };
              };
            }
          )
            .from(LINE_ITEMS_TABLE)
            .select("*")
            .eq("pipeline_property_id", pipelinePropertyId)
            .order("created_at", {
              ascending: true,
            })) as PostgrestSingleResponse<RehabLineItemRow[]>;
          if (error) throw error;
          return data ?? [];
        },
      },
    ],
  });

  const rehabRows = useMemo<RehabLineItemRow[]>(
    () => lineItemsQuery.data ?? [],
    [lineItemsQuery.data],
  );

  // Compose the raw DB columns into the nested strategy shape the UI expects,
  // so every downstream consumer (panels, mergeWithPersisted, dirty tracking)
  // is unchanged by the column split.
  const composedRow = useMemo<DealSettingsRow | null>(() => {
    const raw = settingsQuery.data;
    if (!raw) return null;
    const { flip, brrrr, rental } = composeStrategySettings(raw);
    return { ...raw, flip, brrrr, rental };
  }, [settingsQuery.data]);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: settingsKey });
    void queryClient.invalidateQueries({ queryKey: lineItemsKey });
  }, [queryClient, settingsKey, lineItemsKey]);

  const saveSettings = useMutation({
    mutationFn: async (patch: DealSettingsPatch) => {
      if (!pipelinePropertyId || !currentWorkspace?.id) {
        throw new Error("Missing pipeline_property_id / workspace");
      }
      const { rehab, ...settingsPatch } = patch;
      const existing = settingsQuery.data;

      // Split the nested strategy objects into the dedicated DB columns
      // (flip_hard_money, brrrr_long_term, …); pass the remaining scalar/blob
      // fields (headline inputs, monthly_cash_flow, mao, …) through unchanged.
      const { flip, brrrr, rental, ...scalarPatch } = settingsPatch;
      const dbPatch: Record<string, unknown> = {
        ...scalarPatch,
        ...splitStrategySettings({ flip, brrrr, rental }),
      };

      // 1. property_setting upsert (skipped only if the user's edits were
      //    rehab-only AND a row already exists; otherwise we always write so
      //    `updated_at` advances).
      const hasSettingsKeys = Object.keys(dbPatch).length > 0;
      if (hasSettingsKeys || !existing) {
        if (existing) {
          const { error } = (await (
            supabase as unknown as {
              from: (t: string) => {
                update: (v: unknown) => {
                  eq: (col: string, v: string) => DbResp<unknown>;
                };
              };
            }
          )
            .from(TABLE)
            .update(dbPatch)
            .eq("id", existing.id)) as PostgrestSingleResponse<unknown>;
          if (error) throw error;
        } else {
          const payload = {
            pipeline_property_id: pipelinePropertyId,
            workspace_id: currentWorkspace.id,
            created_by: user?.id ?? null,
            ...dbPatch,
          };
          const { error } = (await (
            supabase as unknown as {
              from: (t: string) => { insert: (v: unknown) => DbResp<unknown> };
            }
          )
            .from(TABLE)
            .insert(payload)) as PostgrestSingleResponse<unknown>;
          if (error) throw error;
        }
      }

      // 2. rehab_line_items diff — sequential: insert, update, delete.
      if (rehab?.insert?.length) {
        const insertRows = rehab.insert.map((entry) => ({
          workspace_id: currentWorkspace.id,
          pipeline_property_id: pipelinePropertyId,
          created_by: user?.id ?? null,
          category: entry.category,
          label: entry.label,
          estimated_cost: entry.amount,
        }));
        const { error } = await supabase
          .from(LINE_ITEMS_TABLE)
          .insert(insertRows as never);
        if (error) throw error;
      }

      if (rehab?.update?.length) {
        for (const entry of rehab.update) {
          const { error } = await supabase
            .from(LINE_ITEMS_TABLE)
            .update({
              label: entry.label,
              estimated_cost: entry.amount,
            } as never)
            .eq("id", entry.id);
          if (error) throw error;
        }
      }

      if (rehab?.delete?.length) {
        const { error } = await supabase
          .from(LINE_ITEMS_TABLE)
          .delete()
          .in("id", rehab.delete);
        if (error) throw error;
      }
    },
    onSuccess: invalidate,
    onError: (err: unknown) =>
      toast.error(toUserMessage(err, "Failed to save deal settings")),
  });

  // Reassemble the legacy-shaped `settings` view that the UI expects, with
  // defaults applied so consumers can always read every field. `rehab` is
  // composed live from the rehab_line_items rows.
  const settings = useMemo(() => {
    const row = composedRow;
    return {
      ...DEFAULT_DEAL_SETTINGS,
      ...(row ?? {}),
      flip: { ...DEFAULT_DEAL_SETTINGS.flip, ...(row?.flip ?? {}) },
      brrrr: { ...DEFAULT_DEAL_SETTINGS.brrrr, ...(row?.brrrr ?? {}) },
      rental: { ...DEFAULT_DEAL_SETTINGS.rental, ...(row?.rental ?? {}) },
      rehab: rowsToRehabSettings(rehabRows),
      monthly_cash_flow: {
        ...DEFAULT_DEAL_SETTINGS.monthly_cash_flow,
        ...(row?.monthly_cash_flow ?? {}),
      },
      mao: { ...DEFAULT_DEAL_SETTINGS.mao, ...(row?.mao ?? {}) },
    };
  }, [composedRow, rehabRows]);

  return {
    settings,
    row: composedRow,
    rehabRows,
    isLoading: settingsQuery.isLoading || lineItemsQuery.isLoading,
    error: settingsQuery.error ?? lineItemsQuery.error,
    saveSettings,
    refetch: () => {
      void settingsQuery.refetch();
      void lineItemsQuery.refetch();
    },
  };
}
